import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const DEFAULT_SITE_NAME = "OctoGames";
const DEFAULT_BASE_URL = "https://octogames.io";
const DEFAULT_IMAGE_PATH = "/page.webp";

const getBaseUrl = () => {
  const envUrl = (process.env.REACT_APP_SITE_URL || "").trim();
  const base =
    envUrl ||
    (typeof window !== "undefined" ? window.location.origin : DEFAULT_BASE_URL);
  return base.replace(/\/+$/, "");
};

const toAbsoluteUrl = (value, baseUrl) => {
  if (!value) return `${baseUrl}/`;
  if (/^https?:\/\//i.test(value)) return value;
  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  return `${baseUrl}${normalizedPath}`;
};

const upsertMeta = (attrName, attrValue, content) => {
  if (!attrValue || typeof document === "undefined") return;
  let tag = document.head.querySelector(`meta[${attrName}="${attrValue}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attrName, attrValue);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
};

const upsertCanonical = (href) => {
  if (typeof document === "undefined") return;
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
};

const Seo = ({
  title,
  description,
  path,
  image,
  type = "website",
  noindex = false,
  keywords = "",
  jsonLd = []
}) => {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === "undefined") return;

    const baseUrl = getBaseUrl();
    const pagePath = path || `${location.pathname}${location.search || ""}`;
    const canonicalUrl = toAbsoluteUrl(pagePath, baseUrl);
    const imageUrl = toAbsoluteUrl(image || DEFAULT_IMAGE_PATH, baseUrl);
    const fullTitle = title ? `${title} | ${DEFAULT_SITE_NAME}` : DEFAULT_SITE_NAME;

    document.title = fullTitle;
    upsertCanonical(canonicalUrl);
    upsertMeta("name", "description", description || "Play HTML5 games online.");
    upsertMeta("name", "robots", noindex ? "noindex,nofollow" : "index,follow");
    upsertMeta("name", "keywords", keywords);

    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:site_name", DEFAULT_SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description || "");
    upsertMeta("property", "og:url", canonicalUrl);
    upsertMeta("property", "og:image", imageUrl);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description || "");
    upsertMeta("name", "twitter:image", imageUrl);

    const oldScripts = document.head.querySelectorAll("script[data-seo-json-ld]");
    oldScripts.forEach((script) => script.remove());
    (Array.isArray(jsonLd) ? jsonLd : [])
      .filter(Boolean)
      .forEach((item) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-seo-json-ld", "true");
        script.text = JSON.stringify(item);
        document.head.appendChild(script);
      });
  }, [description, image, jsonLd, location.pathname, location.search, noindex, path, title, type, keywords]);

  return null;
};

export default Seo;
